import { useEffect, useRef, useState } from "react";
import {
  findProduct,
  LAUNCH_PLAN_ID,
  LAUNCH_DATE_LABEL,
  MEMBERSHIP_DAYS,
} from "../data/products.js";
import { useJoinLaunchPlan } from "../hooks/useJoinLaunchPlan.js";
import { useAuth } from "../context/AuthContext.jsx";
import ProductImage from "./ProductImage.jsx";

const DELAY_MS = 2000;

// Shown once per browser session. sessionStorage (not localStorage) is
// deliberate: navigating around the site won't re-trigger the popup, but a
// fresh visit gets the offer again — and it makes testing easy, since a new
// incognito window always starts clean.
const SEEN_KEY = "rf_launch99_seen";

const alreadySeen = () => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false; // private mode with storage blocked — just show it
  }
};
const markSeen = () => {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
};

// Welcome popup for the ₹9 pre-launch plan. Unlike the old signup gift this
// is pitched to everyone — signed-in customers are exactly the people we want
// as founding members — so there's no auth gate, only the once-per-session key.
export default function WelcomeOffer() {
  const [open, setOpen] = useState(false);
  const closeBtn = useRef(null);
  const joinLaunchPlan = useJoinLaunchPlan();
  const { membership } = useAuth();

  // Already claimed (any qualifying order, past or present) — the offer is
  // one-per-household, so "join for ₹9" would just error out at checkout.
  const claimed = Boolean(membership?.claimed);

  useEffect(() => {
    if (alreadySeen() || claimed) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [claimed]);

  const close = () => {
    markSeen();
    setOpen(false);
  };

  // Membership status loads async (after orders fetch) — if it resolves to
  // "already claimed" right after the popup opened, close it instead of
  // pitching an offer that would just error out at checkout.
  useEffect(() => {
    if (claimed && open) close();
  }, [claimed, open]);

  const join = () => {
    close();
    joinLaunchPlan();
  };

  // While open: lock background scroll, close on Escape, and move focus into
  // the dialog so keyboard users aren't left behind on the page underneath.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtn.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const plan = findProduct(LAUNCH_PLAN_ID);
  // Portrait crop for the tall media panel — the catalogue default is square.
  const portrait = plan?.photo?.replace("w=640&h=640", "w=620&h=880");

  return (
    <div
      className="wpop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="wpop__card" role="dialog" aria-modal="true" aria-labelledby="wpop-head">
        <button
          type="button"
          className="wpop__close"
          onClick={close}
          ref={closeBtn}
          aria-label="Close offer"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
               strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <figure className="wpop__media">
          {plan && <ProductImage product={plan} override={portrait} className="wpop__img" />}
          <span className="wpop__scrim" aria-hidden="true" />
          <figcaption className="wpop__value">
            <span className="wpop__value-num">₹9</span>
            <span className="wpop__value-word">to join</span>
            <span className="wpop__value-was">₹249 value</span>
          </figcaption>
        </figure>

        <div className="wpop__body">
          <span className="wpop__tag">Launching {LAUNCH_DATE_LABEL} · Limited members only</span>
          <h2 id="wpop-head" className="wpop__head">
            Be first at the farm gate
            <em>12 eggs on us</em>
          </h2>

          <ul className="wpop__list">
            <li><strong>12 free pasture eggs</strong> with your first delivery</li>
            <li><strong>Free home delivery</strong> for your first {MEMBERSHIP_DAYS} days</li>
            <li>Deliveries begin <strong>{LAUNCH_DATE_LABEL}</strong> — routes reserved for members</li>
          </ul>

          <button type="button" className="wpop__cta" onClick={join}>
            Reserve my spot — ₹9
          </button>
          <div className="wpop__trust">
            <span>🔒 Secure UPI &amp; card payment</span>
            <span>💯 Full refund if we can't deliver to you</span>
          </div>
          <button type="button" className="wpop__later" onClick={close}>
            Maybe later
          </button>
          <p className="wpop__fine">
            Min. 12 eggs per delivery · Limited launch memberships · One per household
          </p>
        </div>
      </div>
    </div>
  );
}
